/**
 * Release 1 "Find and tidy" components (docs/specs/release1-contract.md): board filter spine, saved views, pins,
 * archive + trash, duplicates, tag merge and bulk AI-tag review. Names match `components.schemas`.
 */
import { z } from 'zod';
import { ActorRef, BoardAssetSort, Id, Int64, Timestamp, cursorPage } from './common.js';
import { Tag } from './tags.js';
import { Board, BoardRef } from './boards.js';
import { Asset, AssetCard, AssetCardPage } from './assets.js';
import { Facets, SearchFilters } from './search.js';

// ---------------------------------------------------------------------------
// Filter spine (F1) + multi-board membership (S6)
// ---------------------------------------------------------------------------

/** `AssetCardPage` plus `facets` when `facets=true` (first page only). */
export const BoardAssetsPage = AssetCardPage.extend({ facets: Facets.optional() });
export type BoardAssetsPage = z.infer<typeof BoardAssetsPage>;

/** Keys of `SearchFilters` that `listBoardAssets` / board views reject (board scope comes from the path). */
export const BOARD_SCOPE_FILTER_KEYS = ['board_ids', 'include_descendants', 'include_deleted'] as const;

/** `SearchFilters` as accepted by `listBoardAssets?filters=` and stored in a board view. */
export const BoardFilters = SearchFilters.omit({
  board_ids: true,
  include_descendants: true,
  include_deleted: true,
});
export type BoardFilters = z.infer<typeof BoardFilters>;

export const AssetBoards = z.object({
  home_board_id: Id.nullable(),
  items: z.array(BoardRef),
});
export type AssetBoards = z.infer<typeof AssetBoards>;

export const AssetBoardsSet = z.object({
  board_ids: z.array(Id).min(1).max(200),
});
export type AssetBoardsSet = z.infer<typeof AssetBoardsSet>;

// ---------------------------------------------------------------------------
// Saved views (F2)
// ---------------------------------------------------------------------------

export const BoardViewVisibility = z.enum(['private', 'board']);
export type BoardViewVisibility = z.infer<typeof BoardViewVisibility>;

export const BoardViewLayout = z.enum(['grid', 'list']);
export type BoardViewLayout = z.infer<typeof BoardViewLayout>;

export const BoardViewDensity = z.enum(['compact', 'comfortable', 'spacious']);
export type BoardViewDensity = z.infer<typeof BoardViewDensity>;

export const BoardViewGroupBy = z.enum(['type', 'approval', 'uploaded_by', 'date_added']);
export type BoardViewGroupBy = z.infer<typeof BoardViewGroupBy>;

export const BOARD_VIEW_NAME_MAX = 120;

export const BoardView = z.object({
  id: Id,
  workspace_id: Id,
  board_id: Id,
  owner_member_id: Id.nullable(),
  name: z.string().max(BOARD_VIEW_NAME_MAX),
  visibility: BoardViewVisibility,
  filters: SearchFilters,
  sort: BoardAssetSort,
  layout: BoardViewLayout,
  density: BoardViewDensity,
  show_titles: z.boolean(),
  group_by: BoardViewGroupBy.nullable(),
  is_default: z.boolean(),
  sort_order: z.int(),
  created_by: Id.nullable().optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type BoardView = z.infer<typeof BoardView>;

export const BoardViewCreate = z.object({
  name: z.string().min(1).max(BOARD_VIEW_NAME_MAX),
  visibility: BoardViewVisibility.default('private'),
  filters: BoardFilters.optional(),
  sort: BoardAssetSort.optional(),
  layout: BoardViewLayout.default('grid'),
  density: BoardViewDensity.default('comfortable'),
  show_titles: z.boolean().default(false),
  group_by: BoardViewGroupBy.nullable().optional(),
  is_default: z.boolean().default(false),
});
export type BoardViewCreate = z.infer<typeof BoardViewCreate>;

export const BoardViewUpdate = z
  .object({
    name: z.string().min(1).max(BOARD_VIEW_NAME_MAX).optional(),
    visibility: BoardViewVisibility.optional(),
    filters: BoardFilters.optional(),
    sort: BoardAssetSort.optional(),
    layout: BoardViewLayout.optional(),
    density: BoardViewDensity.optional(),
    show_titles: z.boolean().optional(),
    group_by: BoardViewGroupBy.nullable().optional(),
    is_default: z.boolean().optional(),
    sort_order: z.int().min(0).optional(),
  })
  .refine((o) => Object.values(o).some((v) => v !== undefined), {
    message: 'at least one field is required',
  });
export type BoardViewUpdate = z.infer<typeof BoardViewUpdate>;

// ---------------------------------------------------------------------------
// Pins (S1)
// ---------------------------------------------------------------------------

export const PinKind = z.enum(['board', 'asset']);
export type PinKind = z.infer<typeof PinKind>;

export const PINS_MAX_PER_MEMBER = 100;

export const MemberPin = z.object({
  id: Id,
  kind: PinKind,
  target_id: Id,
  sort_order: z.int(),
  created_at: Timestamp,
  board: BoardRef.optional(),
  asset: AssetCard.optional(),
  archived_at: Timestamp.nullable().optional(),
});
export type MemberPin = z.infer<typeof MemberPin>;

export const PinReorderRequest = z.object({
  pin_ids: z.array(Id).min(1).max(PINS_MAX_PER_MEMBER),
});
export type PinReorderRequest = z.infer<typeof PinReorderRequest>;

// ---------------------------------------------------------------------------
// Trash (S7a)
// ---------------------------------------------------------------------------

export const TrashItemKind = z.enum(['board', 'asset']);
export type TrashItemKind = z.infer<typeof TrashItemKind>;

export const TRASH_RETENTION_DAYS = 30;

export const TrashItemRef = z.object({
  kind: TrashItemKind,
  id: Id,
});
export type TrashItemRef = z.infer<typeof TrashItemRef>;

export const TrashItem = z.object({
  kind: TrashItemKind,
  id: Id,
  name: z.string(),
  deleted_at: Timestamp,
  purge_at: Timestamp,
  deleted_by: ActorRef.optional(),
  deleted_with: Id.nullable().optional(),
  merged_into_asset_id: Id.nullable().optional(),
  board: BoardRef.optional(),
  asset: AssetCard.optional(),
  parent_deleted: z.boolean().optional(),
});
export type TrashItem = z.infer<typeof TrashItem>;

export const TrashPage = cursorPage(TrashItem);
export type TrashPage = z.infer<typeof TrashPage>;

export const TrashBulkRequest = z.object({
  items: z.array(TrashItemRef).max(5000).optional(),
  all: z.boolean().default(false),
});
export type TrashBulkRequest = z.infer<typeof TrashBulkRequest>;

export const TrashBulkResult = z.object({
  succeeded: z.array(TrashItemRef),
  failed: z.array(
    z.object({
      kind: TrashItemKind,
      id: Id,
      code: z.string(),
      message: z.string().optional(),
    }),
  ),
});
export type TrashBulkResult = z.infer<typeof TrashBulkResult>;

// ---------------------------------------------------------------------------
// Duplicates (F5)
// ---------------------------------------------------------------------------

export const DuplicateMode = z.enum(['exact', 'near']);
export type DuplicateMode = z.infer<typeof DuplicateMode>;

/** Default / max pHash Hamming distance; multi-index hashing guarantees recall up to 7. */
export const DUPLICATE_THRESHOLD_DEFAULT = 10;
export const DUPLICATE_THRESHOLD_MAX = 16;

export const DuplicateMember = z.object({
  asset: AssetCard,
  distance: z.int().min(0),
  sha256: z.string().nullable().optional(),
  size_bytes: Int64.nullable().optional(),
  width: z.int().nullable().optional(),
  height: z.int().nullable().optional(),
});
export type DuplicateMember = z.infer<typeof DuplicateMember>;

export const DuplicateGroup = z.object({
  id: z.string(),
  mode: DuplicateMode,
  representative: DuplicateMember,
  members: z.array(DuplicateMember),
});
export type DuplicateGroup = z.infer<typeof DuplicateGroup>;

export const DuplicateGroupPage = cursorPage(DuplicateGroup);
export type DuplicateGroupPage = z.infer<typeof DuplicateGroupPage>;

export const DuplicateMergeRequest = z
  .object({
    keep_asset_id: Id,
    merge_asset_ids: z.array(Id).min(1).max(100),
  })
  .refine((o) => !o.merge_asset_ids.includes(o.keep_asset_id), {
    message: 'keep_asset_id cannot be merged into itself',
    path: ['merge_asset_ids'],
  });
export type DuplicateMergeRequest = z.infer<typeof DuplicateMergeRequest>;

export const DuplicateMergeResult = z.object({
  kept: Asset,
  merged_asset_ids: z.array(Id),
  boards_moved: z.int(),
  tags_moved: z.int(),
  fields_moved: z.int(),
  comments_moved: z.int(),
});
export type DuplicateMergeResult = z.infer<typeof DuplicateMergeResult>;

export const DuplicateDismissRequest = z.object({
  asset_ids: z.array(Id).min(2).max(100),
});
export type DuplicateDismissRequest = z.infer<typeof DuplicateDismissRequest>;

export const AssetLookupMatch = z.object({
  asset: AssetCard,
  match: z.enum(['exact', 'near']),
  distance: z.int().min(0),
});
export type AssetLookupMatch = z.infer<typeof AssetLookupMatch>;

export const AssetLookupResponse = z.object({
  items: z.array(AssetLookupMatch).max(20),
});
export type AssetLookupResponse = z.infer<typeof AssetLookupResponse>;

/** Query of `GET /workspaces/{workspaceId}/assets/lookup` (at least one of sha256 / phash). */
export const AssetLookupQuery = z
  .object({
    sha256: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    phash: z.string().regex(/^[0-9a-f]{16}$/).optional(),
    threshold: z.coerce.number().int().min(0).max(DUPLICATE_THRESHOLD_MAX).default(DUPLICATE_THRESHOLD_DEFAULT),
  })
  .refine((q) => q.sha256 !== undefined || q.phash !== undefined, {
    message: 'sha256 or phash is required',
  });
export type AssetLookupQuery = z.infer<typeof AssetLookupQuery>;

// ---------------------------------------------------------------------------
// Tags (G2)
// ---------------------------------------------------------------------------

export const TagMergeRequest = z
  .object({
    into_tag_id: Id,
    tag_ids: z.array(Id).min(1).max(100),
  })
  .refine((o) => !o.tag_ids.includes(o.into_tag_id), {
    message: 'into_tag_id cannot be merged into itself',
    path: ['tag_ids'],
  });
export type TagMergeRequest = z.infer<typeof TagMergeRequest>;

export const TagMergeResult = z.object({
  tag: Tag,
  merged_tag_ids: z.array(Id),
  assets_retagged: z.int(),
});
export type TagMergeResult = z.infer<typeof TagMergeResult>;

export const AiTagReviewAction = z.enum(['keep', 'dismiss']);
export type AiTagReviewAction = z.infer<typeof AiTagReviewAction>;

export const AiTagReviewBucket = z.object({
  tag: Tag,
  pending_count: z.int(),
  avg_confidence: z.number().nullable().optional(),
  samples: z.array(AssetCard).max(6),
});
export type AiTagReviewBucket = z.infer<typeof AiTagReviewBucket>;

export const AiTagReviewPage = cursorPage(AiTagReviewBucket);
export type AiTagReviewPage = z.infer<typeof AiTagReviewPage>;

export const AiTagReviewRequest = z.object({
  tag_id: Id,
  action: AiTagReviewAction,
  asset_ids: z.array(Id).max(5000).optional(),
  board_id: Id.nullable().optional(),
});
export type AiTagReviewRequest = z.infer<typeof AiTagReviewRequest>;

export const AiTagReviewResult = z.object({
  tag_id: Id,
  action: AiTagReviewAction,
  affected: z.int(),
  skipped: z.int(),
});
export type AiTagReviewResult = z.infer<typeof AiTagReviewResult>;

// ---------------------------------------------------------------------------
// WebSocket payloads
// ---------------------------------------------------------------------------

export const WsBoardViewPayload = z.object({
  board_id: Id,
  view_id: Id,
  change: z.enum(['created', 'updated', 'deleted']),
  view: BoardView.optional(),
});
export type WsBoardViewPayload = z.infer<typeof WsBoardViewPayload>;

export const WsPinPayload = z.object({
  workspace_id: Id,
  member_id: Id,
  items: z.array(MemberPin),
});
export type WsPinPayload = z.infer<typeof WsPinPayload>;

export const WsBoardArchivedPayload = z.object({
  board_id: Id,
  parent_board_id: Id.nullable(),
  archived_subtree_ids: z.array(Id),
  archived: z.boolean(),
  board: Board.optional(),
});
export type WsBoardArchivedPayload = z.infer<typeof WsBoardArchivedPayload>;

export const WsAssetMergedPayload = z.object({
  asset_id: Id,
  merged_asset_ids: z.array(Id),
  asset: AssetCard,
});
export type WsAssetMergedPayload = z.infer<typeof WsAssetMergedPayload>;

export const WsTagMergedPayload = z.object({
  workspace_id: Id,
  tag_id: Id,
  merged_tag_ids: z.array(Id),
  assets_retagged: z.int(),
  tag: Tag.optional(),
});
export type WsTagMergedPayload = z.infer<typeof WsTagMergedPayload>;

export const WsTrashPurgedPayload = z.object({
  workspace_id: Id,
  items: z.array(TrashItemRef),
});
export type WsTrashPurgedPayload = z.infer<typeof WsTrashPurgedPayload>;
